import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import {
  useRoles,
  usePermissionsCatalog,
  useCreateRole,
  useUpdateRolePermissions,
  useDeleteRole,
  RoleRecord,
  CreateRolePayload,
} from "@/api/roles";
import { getApiErrorMessage } from "@/api/client";
import { useAuthStore } from "@/stores/authStore";

export default function RolesPage() {
  const { data: roles, isLoading } = useRoles();
  const { data: permissionsCatalog } = usePermissionsCatalog();
  const [showCreate, setShowCreate] = useState(false);
  const [editingRole, setEditingRole] = useState<RoleRecord | null>(null);
  const hasPermission = useAuthStore((s) => s.hasPermission);

  const groupedPermissions = useMemo(() => {
    const groups: Record<string, typeof permissionsCatalog> = {};
    (permissionsCatalog ?? []).forEach((p) => {
      groups[p.resource] = groups[p.resource] ?? [];
      groups[p.resource]!.push(p);
    });
    return groups;
  }, [permissionsCatalog]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-gray-900">Roles y permisos</h1>
        {hasPermission("roles.create") && (
          <button type="button" className="btn-primary" onClick={() => setShowCreate(true)}>
            + Nuevo rol
          </button>
        )}
      </div>

      <div className="card overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Rol</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Tipo</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Permisos</th>
              <th className="px-4 py-3 text-right font-medium text-gray-500">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr>
                <td colSpan={4} className="px-4 py-6 text-center text-gray-400">
                  Cargando...
                </td>
              </tr>
            )}
            {(roles ?? []).map((role) => (
              <tr key={role.id}>
                <td className="px-4 py-3 font-medium text-gray-900">{role.name}</td>
                <td className="px-4 py-3 text-gray-600">
                  {role.isSystem ? (
                    <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-700">Sistema</span>
                  ) : (
                    <span className="rounded-full bg-purple-100 px-2 py-0.5 text-xs text-purple-700">Personalizado</span>
                  )}
                </td>
                <td className="px-4 py-3 text-gray-600">
                  {role.permissions.length === 0 && role.isSystem ? "Todos (SUPER_ADMIN)" : `${role.permissions.length} permisos`}
                </td>
                <td className="px-4 py-3 text-right space-x-2">
                  {!role.isSystem && hasPermission("roles.update") && (
                    <button
                      type="button"
                      className="text-brand-600 hover:text-brand-800 text-xs font-medium"
                      onClick={() => setEditingRole(role)}
                    >
                      Editar permisos
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <CreateRoleModal groupedPermissions={groupedPermissions} onClose={() => setShowCreate(false)} />
      )}
      {editingRole && (
        <EditRolePermissionsModal
          role={editingRole}
          groupedPermissions={groupedPermissions}
          onClose={() => setEditingRole(null)}
        />
      )}
    </div>
  );
}

function PermissionChecklist({
  groupedPermissions,
  selected,
  onToggle,
}: {
  groupedPermissions: Record<string, Array<{ id: string; key: string; action: string }> | undefined>;
  selected: Set<string>;
  onToggle: (id: string) => void;
}) {
  return (
    <div className="max-h-64 overflow-y-auto rounded-md border border-gray-200 p-3 space-y-3">
      {Object.entries(groupedPermissions).map(([resource, perms]) => (
        <div key={resource}>
          <p className="text-xs font-semibold uppercase text-gray-400">{resource}</p>
          <div className="mt-1 grid grid-cols-2 gap-1">
            {(perms ?? []).map((p) => (
              <label key={p.id} className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={selected.has(p.id)} onChange={() => onToggle(p.id)} />
                {p.action}
              </label>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function CreateRoleModal({
  groupedPermissions,
  onClose,
}: {
  groupedPermissions: Record<string, Array<{ id: string; key: string; action: string }> | undefined>;
  onClose: () => void;
}) {
  const { register, handleSubmit } = useForm<CreateRolePayload>();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const createRole = useCreateRole();

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const onSubmit = async (values: CreateRolePayload) => {
    try {
      await createRole.mutateAsync({ ...values, permissionIds: Array.from(selected) });
      toast.success("Rol creado correctamente.");
      onClose();
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4">
      <div className="card w-full max-w-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900">Nuevo rol</h2>
        <form className="mt-4 space-y-4" onSubmit={handleSubmit(onSubmit)}>
          <div>
            <label className="block text-sm font-medium text-gray-700">Código (mayúsculas, sin espacios)</label>
            <input className="input-field mt-1" placeholder="SUPERVISOR_TRAMITES" {...register("code", { required: true })} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Nombre</label>
            <input className="input-field mt-1" {...register("name", { required: true })} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Permisos</label>
            <PermissionChecklist groupedPermissions={groupedPermissions} selected={selected} onToggle={toggle} />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="btn-primary" disabled={createRole.isPending}>
              {createRole.isPending ? "Creando..." : "Crear rol"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditRolePermissionsModal({
  role,
  groupedPermissions,
  onClose,
}: {
  role: RoleRecord;
  groupedPermissions: Record<string, Array<{ id: string; key: string; action: string }> | undefined>;
  onClose: () => void;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set(role.permissions.map((p) => p.id)));
  const updatePermissions = useUpdateRolePermissions();
  const deleteRole = useDeleteRole();

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const handleSave = async () => {
    try {
      await updatePermissions.mutateAsync({ id: role.id, permissionIds: Array.from(selected) });
      toast.success("Permisos actualizados.");
      onClose();
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`¿Eliminar el rol "${role.name}"?`)) return;
    try {
      await deleteRole.mutateAsync(role.id);
      toast.success("Rol eliminado.");
      onClose();
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4">
      <div className="card w-full max-w-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900">Editar permisos — {role.name}</h2>
        <div className="mt-4">
          <PermissionChecklist groupedPermissions={groupedPermissions} selected={selected} onToggle={toggle} />
        </div>
        <div className="flex justify-between gap-2 pt-4">
          <button type="button" className="text-red-600 hover:text-red-800 text-sm font-medium" onClick={handleDelete}>
            Eliminar rol
          </button>
          <div className="flex gap-2">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button type="button" className="btn-primary" onClick={handleSave} disabled={updatePermissions.isPending}>
              {updatePermissions.isPending ? "Guardando..." : "Guardar"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
